import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { WebsiteDraft, WebsiteWizardStep } from './types';
import type { Website, WebsiteModule } from '../api/websites';
import { FormErrorCallout } from '../components/FormErrorCallout';

// WebsiteWizard is a thin chrome around four child steps. Mirrors the
// onboarding/Wizard.tsx pattern (progress dots, footer nav, per-step
// validation gating Continue) but typed against WebsiteDraft instead of
// OnboardingDraft. A future refactor could unify them via a generic
// Wizard<T> in components/; v1 keeps the duplication so the websites
// module's blast radius stays local.

export interface WebsiteWizardProps {
  steps: readonly WebsiteWizardStep[];
  draft: WebsiteDraft;
  update: (patch: Partial<WebsiteDraft>) => void;
  toggleModule: (key: WebsiteModule) => void;
  toggleTeam: (teamID: string, on: boolean) => void;
  saveDraft: () => Promise<Website | null>;
  onSubmit: (draft: WebsiteDraft) => Promise<void> | void;
  submittingLabelKey?: string;
  submitErrorCode?: string | null;
}

export function WebsiteWizard({
  steps,
  draft,
  update,
  toggleModule,
  toggleTeam,
  saveDraft,
  onSubmit,
  submittingLabelKey = 'common.submitting',
  submitErrorCode,
}: WebsiteWizardProps) {
  const { t } = useTranslation('common');
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // Bumped each time the error code changes so the callout (which
  // scrolls into view on mount) re-fires its effect — covers the
  // user-hits-Save-twice-on-the-same-error case where React would
  // otherwise re-use the existing instance and skip the scroll.
  const [errorTick, setErrorTick] = useState(0);
  useEffect(() => {
    if (submitErrorCode) setErrorTick((n) => n + 1);
  }, [submitErrorCode]);

  const step = steps[stepIndex];
  if (!step) return null;

  const isLast = stepIndex === steps.length - 1;
  const canContinue = step.isValid(draft);

  const back = () => setStepIndex((i) => Math.max(0, i - 1));
  const next = async () => {
    if (!canContinue) return;
    if (isLast) {
      setSubmitting(true);
      try {
        await onSubmit(draft);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setStepIndex((i) => i + 1);
  };

  return (
    <section className="wizard">
      <ol className="wizard__progress" aria-label={t('website.wizard.progressLabel')}>
        {steps.map((s, i) => (
          <li
            key={s.id}
            className={
              'wizard__dot' +
              (i === stepIndex ? ' wizard__dot--active' : '') +
              (i < stepIndex ? ' wizard__dot--done' : '')
            }
            aria-current={i === stepIndex ? 'step' : undefined}
          >
            <span className="wizard__dot-num">{i + 1}</span>
            <span className="wizard__dot-label">{t(s.titleKey)}</span>
          </li>
        ))}
      </ol>

      <header className="wizard__header">
        <h2>{t(step.titleKey)}</h2>
        {step.descriptionKey && <p>{t(step.descriptionKey)}</p>}
      </header>

      <step.Component
        draft={draft}
        update={update}
        toggleModule={toggleModule}
        toggleTeam={toggleTeam}
        saveDraft={saveDraft}
      />

      {submitErrorCode && (
        <FormErrorCallout
          key={errorTick}
          heading={t('website.wizard.error.heading')}
        >
          <p>
            {t(`website.wizard.error.${submitErrorCode}`, {
              defaultValue: t('website.wizard.error.unknown'),
            })}
          </p>
        </FormErrorCallout>
      )}

      <footer className="wizard__nav">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={back}
          disabled={stepIndex === 0 || submitting}
        >
          {t('common.back')}
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void next()}
          disabled={!canContinue || submitting}
        >
          {submitting
            ? t(submittingLabelKey)
            : isLast
              ? t('website.wizard.publish')
              : t('common.continue')}
        </button>
      </footer>
    </section>
  );
}
